import type { MenuItem } from '../schemas/menu/menuItemSchema';

//
// Small generic helpers
//

/**
 * Logs the entire JSON object.
 * Avoids the issue of nested objects logging as '[object]'.
 * @param object
 */
export const logJson = (object: unknown) => {
  console.dir(object, { depth: null });
};

export const capitaliseFirstLetter = (string: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Returns a short version of an uuid for easier reading
 * @param id
 * @example shortId('17abf563-ab88-43c1-83e9-c1d908deab14')
 * // returns '17abf563'
 */
export const shortId = (id: string): string => {
  const shortHash = id.split('-')[0];
  if (!shortHash) return id;
  return shortHash;
};

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

type LinkedMenuItemNamesParams = {
  /** The current menu item can be optionally excluded from result set. */
  currentMenuItemUuid?: string;
  menuItems: MenuItem[];
  /** Can be either `modifierGroupId` or `sizeModifierGroupId`. */
  modifierGroupUuid: string;
};

const getLinkedMenuItems = ({
  currentMenuItemUuid,
  menuItems,
  modifierGroupUuid,
}: LinkedMenuItemNamesParams): MenuItem[] => {
  const filteredMenuItems = menuItems.filter((mi) => mi.uuid !== currentMenuItemUuid);

  // First, check for sizeModifierGroupId.
  let linkedMenuItems: MenuItem[] = filteredMenuItems.filter(
    (mi) => mi.sizeModifierGroupUuid === modifierGroupUuid,
  );
  // Then, check for modifierGroupIds.
  if (linkedMenuItems.length === 0) {
    linkedMenuItems = filteredMenuItems.filter((mi) =>
      mi.modifierGroupUuids.includes(modifierGroupUuid),
    );
  }
  return linkedMenuItems;
};

export const getLinkedMenuItemNames = ({
  currentMenuItemUuid,
  menuItems,
  modifierGroupUuid,
}: LinkedMenuItemNamesParams): string[] => {
  const linkedMenuItems = getLinkedMenuItems({ currentMenuItemUuid, menuItems, modifierGroupUuid });
  return linkedMenuItems.map((mi) => mi.name).sort((a, b) => a.localeCompare(b));
};

export const getLinkedMenuItemCount = ({
  currentMenuItemUuid,
  menuItems,
  modifierGroupUuid,
}: LinkedMenuItemNamesParams): number => {
  const linkedMenuItems = getLinkedMenuItems({ currentMenuItemUuid, menuItems, modifierGroupUuid });
  return linkedMenuItems.length;
};

/**
 * TypeScript doesn't return the correct type when filtering out nulls from an array.
 * This function is a workaround to fix that.
 * @param array
 */
export function removeNullsFromArray<T>(array: (T | null)[]): T[] {
  return array.filter((element): element is T => element !== null);
}
